"use client";

import { useEffect, useState } from "react";
import { t, type Locale } from "@/lib/i18n";

export function LoginForm({
  locale,
  initialError,
}: {
  locale: Locale;
  initialError?: string | null;
}) {
  const [employeeId, setEmployeeId] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(initialError ?? null);
  const [needsBootstrap, setNeedsBootstrap] = useState(false);
  const [bootName, setBootName] = useState("");

  useEffect(() => {
    fetch("/api/auth/bootstrap", { cache: "no-store" })
      .then((r) => r.json())
      .then((d) => setNeedsBootstrap(Boolean(d?.needsBootstrap)))
      .catch(() => {});
  }, []);

  async function login(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ employeeId: employeeId.trim(), password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          typeof data.error === "string" ? data.error : "Login failed",
        );
      }
      window.location.assign("/");
    } catch (err) {
      // If fetch is blocked (dev cross-origin), fall back to native form POST.
      const formBlocked =
        err instanceof TypeError ||
        (err instanceof Error && /failed to fetch|network/i.test(err.message));
      if (formBlocked) {
        e.currentTarget.submit();
        return;
      }
      setError(err instanceof Error ? err.message : "Login failed");
      setLoading(false);
    }
  }

  async function bootstrapAdmin(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/auth/bootstrap", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ employeeId, fullName: bootName, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          typeof data.error === "string" ? data.error : "Bootstrap failed",
        );
      }
      setNeedsBootstrap(false);
      const signIn = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ employeeId: employeeId.trim(), password }),
      });
      const signInData = await signIn.json().catch(() => ({}));
      if (!signIn.ok) {
        throw new Error(
          typeof signInData.error === "string"
            ? signInData.error
            : "Login failed",
        );
      }
      window.location.assign("/");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Bootstrap failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <form
      method="post"
      action="/api/auth/login"
      onSubmit={needsBootstrap ? bootstrapAdmin : login}
      autoComplete="on"
      className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm space-y-4"
    >
      <h1 className="text-xl font-semibold">
        {needsBootstrap
          ? t(locale, "login.bootstrapTitle")
          : t(locale, "login.title")}
      </h1>
      <p className="text-sm text-muted">{t(locale, "login.hint")}</p>
      {needsBootstrap && (
        <div>
          <label htmlFor="fullName" className="mb-1 block text-sm font-medium">
            {t(locale, "login.fullName")}
          </label>
          <input
            id="fullName"
            name="fullName"
            autoComplete="name"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary"
            value={bootName}
            onChange={(e) => setBootName(e.target.value)}
          />
        </div>
      )}
      <div>
        <label htmlFor="employeeId" className="mb-1 block text-sm font-medium">
          {t(locale, "login.employeeId")}
        </label>
        <input
          id="employeeId"
          name="employeeId"
          autoComplete="username"
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary"
          value={employeeId}
          onChange={(e) => setEmployeeId(e.target.value)}
          required
        />
      </div>
      <div>
        <label htmlFor="password" className="mb-1 block text-sm font-medium">
          {t(locale, "login.password")}
        </label>
        <input
          id="password"
          name="password"
          type="password"
          autoComplete={needsBootstrap ? "new-password" : "current-password"}
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <button
        type="submit"
        disabled={loading}
        className="btn-primary w-full disabled:opacity-50"
      >
        {loading
          ? t(locale, "login.loading")
          : needsBootstrap
            ? t(locale, "login.bootstrapSubmit")
            : t(locale, "login.submit")}
      </button>
    </form>
  );
}
